import { storageApi } from '@d/sdk'
import { PathGuide } from '@d/util/pathGuide'
import { CacheGroup } from './cache-handler'
import { StorageDownloadCache } from './entries'

export const VERSIONING_FOLDER = '.versioning'
export const VERSIONING_HISTORY_FILE = 'history.csv'
export const VERSIONING_META_FILE = 'meta.json'

interface VersioningHistoryEntry {
  uid: string
  date: string
}

export interface VersioningMeta {
  latestVersion?: {
    date: number
    userId: string
  }
  currentVersion?: {
    date: number
    userId: string
  }
  lastChanged?: {
    date: number
    userId: string
  }
  created?: {
    date: number
    userId: string
  }
  removed?: {
    date: number
    userId: string
  }
}

// Version file name format:
// FILENAME[HHMMSS-DDMMYYYY-USERID].EXTENSION

function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703
  let h2 = 3144134277
  let h3 = 1013904242
  let h4 = 2773480762
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i)
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067)
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233)
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213)
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179)
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067)
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233)
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213)
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  // eslint-disable-next-line no-sequences, ts/no-unused-expressions
  (h1 ^= h2 ^ h3 ^ h4), (h2 ^= h1), (h3 ^= h1), (h4 ^= h1)
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0]
}

export class Versioning {
  constructor(public filePath: string) {
    this.filePath = PathGuide.from(filePath).toString()
  }

  get versioningHistoryFile(): PathGuide {
    return this.versioningFolder.cd(VERSIONING_HISTORY_FILE)
  }

  get versioningFolder(): PathGuide {
    const p = PathGuide.from(this.filePath)
    return p.cd('..').cd(VERSIONING_FOLDER).cd(p.safeName)
  }

  get metaFile(): PathGuide {
    return this.versioningFolder.cd(VERSIONING_META_FILE)
  }

  async updateMeta(
    callback: (currentMeta: Partial<VersioningMeta>) => Partial<VersioningMeta>,
  ): Promise<void> {
    // Read current meta
    let d: Partial<VersioningMeta> = {}
    try {
      const value = await this._download(this.metaFile.toString())

      if (value) {
        const currentMeta: VersioningMeta = JSON.parse(value)
        d = callback(currentMeta)
      }
      else {
        d = callback({})
      }
    }
    catch {
      d = callback({})
    }

    const content = JSON.stringify(d, null, 2)
    const blob = new Blob([content], { type: 'application/json' })

    await storageApi.upload(this.metaFile.toString(), true, false, blob)
  }

  static getUniqueVersionName(date: number, userId: string) {
    // Generate hash from date and userId
    const hash = cyrb128(`${date}-${userId}`)
    const hashNum = Math.abs(hash[0] ^ hash[1] ^ hash[2] ^ hash[3])

    return hashNum.toString(16).padEnd(8, '0')
  }

  async _readHistory(): Promise<VersioningHistoryEntry[] | null> {
    try {
      const value = await this._download(this.versioningHistoryFile.toString())
      if (!value)
        return null
      const lines = value.split('\n').filter(x => x.length)
      return lines.map((line) => {
        const [uid, date] = line.split(',')
        return { uid, date }
      })
    }
    catch {
      return null
    }
  }

  static getVersionName(fileName: string, userId: string, date: Date): string {
    const dateStr = date.getTime().toString()

    // const extension = fileName.split('.').pop()
    // const fileName2 = fileName.replace(`.${extension}`, '')
    return `[${dateStr}-${userId}].bak`
    // return `[${dateStr}-${userId}]${extension ? `.${extension}` : ""}`;
  }

  async getVersions(): Promise<FileVersion[]> {
    const history = await this._readHistory()
    if (!history)
      return []

    const versions: FileVersion[] = []
    for (const entry of history) {
      const date = new Date(Number.parseInt(entry.date))
      const fileName = Versioning.getVersionName(
        PathGuide.from(this.filePath).safeName,
        entry.uid,
        date,
      )

      versions.push(new FileVersion(this.filePath, fileName, date, entry.uid))
    }

    return versions.sort((a, b) => b.date.getTime() - a.date.getTime())
  }

  async _download(path: string): Promise<string | null> {
    const dl = PathGuide.from(path).useDownload()
    const value = await dl.tryValueAsync
    dl.release()
    return value ? await value.blob.text() : null
  }
}

export class FileVersion {
  public id: string = ''

  constructor(
    public fullPath: string,
    public fileName: string,
    public date: Date,
    public userId: string,
  ) {
    this.id = Versioning.getUniqueVersionName(date.getTime(), userId)
  }

  get versioningFolderPath(): PathGuide {
    const pg = PathGuide.from(this.fullPath)
    return pg.cd('..').cd(VERSIONING_FOLDER).cd(pg.safeName)
  }

  get restoreFilePath(): string {
    const pg = PathGuide.from(this.fullPath)
    return this.versioningFolderPath.cd(Versioning.getVersionName(pg.safeName, this.userId, this.date)).toString()
  }

  get latestPath(): string {
    return this.versioningFolderPath.cd(VERSIONING_META_FILE).toString()
  }

  async restore(_instance: string): Promise<void> {
    const cg = new CacheGroup()
    const dlNode = StorageDownloadCache.use(this.restoreFilePath, cg)
    const dlVal = await dlNode.tryValueAsync
    const blob = dlVal?.blob
    cg.clear()

    if (!blob) {
      throw new Error(`Version file not found: ${this.restoreFilePath}`)
    }

    await storageApi.upload(this.fullPath, true, false, blob)

    // Update meta

    await this.updateMeta(currentMeta => ({
      ...currentMeta,
      currentVersion: {
        date: this.date.getTime(),
        userId: this.userId,
      },
      lastChanged: {
        date: this.date.getTime(),
        userId: this.userId,
      },
    }))
    // const content = `${this.date.getTime()}-${this.userId}\n`
    // const blob = new Blob([content], { type: 'text/csv' })
    // await storageApi.upload(this.latestPath, true, false, blob)
  }

  get metaFile(): PathGuide {
    return this.versioningFolderPath.cd(VERSIONING_META_FILE)
  }

  async updateMeta(callback: (currentMeta: Partial<VersioningMeta>) => Partial<VersioningMeta>): Promise<void> {
    // Read current meta
    let d: Partial<VersioningMeta> = {}
    try {
      const dlNode = StorageDownloadCache.use(this.metaFile.toString())
      const dl = await (await dlNode.tryValueAsync)?.blob.text()
      dlNode.release()

      const currentMeta: VersioningMeta = JSON.parse(dl ?? '{}')
      d = callback(currentMeta)
    }
    catch {
      d = callback({})
    }
    const content = JSON.stringify(d, null, 2)

    const body = new Blob([content], { type: 'application/json' })

    await storageApi.upload(
      this.metaFile.toString(),
      true,
      false,
      body,
    )
  }
}
